import React, { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { useTranslation } from 'react-i18next'
import Card from '../../components/ui/Card'
import LoadingSpinner from '../../components/ui/LoadingSpinner'
import { budgetAPI, categoriesAPI } from '../../services/api'
import useLanguage from '../../hooks/useLanguage'

const BudgetsPage = () => {
  const { t } = useTranslation('budget')
  const { locale } = useLanguage()
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [editingBudget, setEditingBudget] = useState(null)
  const [formData, setFormData] = useState({
    name: '',
    amount: '',
    category_id: '',
    period: 'monthly',
    start_date: new Date().toISOString().split('T')[0],
    end_date: '',
    description: ''
  })

  const queryClient = useQueryClient()

  // Pobieranie budżetów
  const { data: budgets, isLoading, error } = useQuery(
    ['budgets'],
    () => budgetAPI.getBudgets(),
    {
      onError: (error) => {
        console.error('Błąd podczas pobierania budżetów:', error)
      }
    }
  )

  // Pobieranie kategorii do selecta
  const { data: categories } = useQuery(
    ['categories'],
    async () => {
      const response = await categoriesAPI.getCategories()
      return response.data?.categories || response.categories || []
    }
  )

  // Mutacja do tworzenia budżetu
  const createMutation = useMutation(
    async (budgetData) => {
      const response = await budgetAPI.createBudget(budgetData)
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['budgets'])
        queryClient.invalidateQueries('dashboard')
        setIsModalOpen(false)
        resetForm()
      }
    }
  )

  // Mutacja do edycji budżetu
  const updateMutation = useMutation(
    async ({ id, ...budgetData }) => {
      const response = await budgetAPI.updateBudget(id, budgetData)
      return response.data
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['budgets'])
        queryClient.invalidateQueries('dashboard')
        setIsModalOpen(false)
        setEditingBudget(null)
        resetForm()
      }
    }
  )

  // Mutacja do usuwania budżetu
  const deleteMutation = useMutation(
    async (id) => {
      await budgetAPI.deleteBudget(id)
    },
    {
      onSuccess: () => {
        queryClient.invalidateQueries(['budgets'])
        queryClient.invalidateQueries('dashboard')
      }
    }
  )

  const resetForm = () => {
    setFormData({
      name: '',
      amount: '',
      category_id: '',
      period: 'monthly',
      start_date: new Date().toISOString().split('T')[0],
      end_date: '',
      description: ''
    })
  }

  const handleSubmit = (e) => {
    e.preventDefault()

    const budgetData = {
      name: formData.name,
      amount: parseFloat(formData.amount),
      period: formData.period,
      start_date: formData.start_date,
      // Tylko dodaj pola jeśli nie są puste
      ...(formData.description && { description: formData.description }),
      ...(formData.end_date && { end_date: formData.end_date }),
      ...(formData.category_id && { category_id: formData.category_id })
    }

    if (editingBudget) {
      updateMutation.mutate({ id: editingBudget.id, ...budgetData })
    } else {
      createMutation.mutate(budgetData)
    }
  }

  const handleEdit = (budget) => {
    setEditingBudget(budget)
    setFormData({
      name: budget.name || '',
      amount: budget.amount.toString(),
      category_id: budget.category_id || '',
      period: budget.period || 'monthly',
      start_date: budget.start_date ? budget.start_date.split('T')[0] : new Date().toISOString().split('T')[0],
      end_date: budget.end_date ? budget.end_date.split('T')[0] : '',
      description: budget.description || ''
    })
    setIsModalOpen(true)
  }

  const handleDelete = (id) => {
    if (window.confirm(t('budgets.confirmDelete'))) {
      deleteMutation.mutate(id)
    }
  }

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: 'PLN'
    }).format(amount)
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString(locale)
  }

  const getPeriodLabel = (period) => {
    return t(`common:periods.${period}`, { defaultValue: period })
  }

  const calculateProgress = (budget) => {
    const spent = budget.current_stats?.current_spent || budget.spent || 0
    if (!spent || !budget.amount) return 0
    return Math.min((spent / parseFloat(budget.amount)) * 100, 100)
  }

  const getProgressColor = (progress) => {
    if (progress >= 90) return 'bg-red-500'
    if (progress >= 75) return 'bg-yellow-500'
    return 'bg-green-500'
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Card>
          <Card.Body>
            <div className="text-center py-12">
              <LoadingSpinner size="lg" text={t('budgets.loading')} />
            </div>
          </Card.Body>
        </Card>
      </div>
    )
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Card>
          <Card.Body>
            <div className="text-center py-12">
              <div className="text-red-500">
                <p>{t('budgets.error')}</p>
                <p className="text-sm mt-2">{error.message}</p>
              </div>
            </div>
          </Card.Body>
        </Card>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <div className="md:flex md:items-center md:justify-between">
        <div className="min-w-0 flex-1">
          <p className="text-sm text-[var(--color-text-muted)]">
            {t('budgets.subtitle')}
          </p>
        </div>
        <div className="mt-4 flex md:ml-4 md:mt-0">
          <button
            type="button"
            onClick={() => setIsModalOpen(true)}
            className="inline-flex items-center rounded-md bg-[#1f9f93] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#167e75] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1f9f93]"
          >
            {t('budgets.add')}
          </button>
        </div>
      </div>

      {/* Lista budżetów */}
      {budgets?.data?.budgets && budgets.data.budgets.length > 0 ? (
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-3">
          {budgets.data.budgets.map((budget) => {
            const progress = calculateProgress(budget)
            const progressColor = getProgressColor(progress)

            return (
              <Card key={budget.id}>
                <Card.Body>
                  <div className="flex items-center justify-between">
                    <h3 className="text-lg font-medium text-[var(--color-text)]">{budget.name}</h3>
                    <div className="flex space-x-2">
                      <button
                        onClick={() => handleEdit(budget)}
                        className="text-[#1f9f93] hover:text-[#167e75] text-sm"
                      >
                        {t('common:edit')}
                      </button>
                      <button
                        onClick={() => handleDelete(budget.id)}
                        className="text-red-600 hover:text-red-900 text-sm"
                      >
                        {t('common:delete')}
                      </button>
                    </div>
                  </div>

                  <div className="mt-4">
                    <div className="flex justify-between text-sm text-[var(--color-text-muted)]">
                      <span>{t('budgets.spent')} {formatCurrency(budget.current_stats?.current_spent || budget.spent || 0)}</span>
                      <span>{t('budgets.limit')} {formatCurrency(budget.amount)}</span>
                    </div>
                    <div className="mt-2 w-full bg-[var(--color-skeleton)] rounded-full h-2">
                      <div
                        className={`h-2 rounded-full ${progressColor}`}
                        style={{ width: `${progress}%` }}
                      ></div>
                    </div>
                    <div className="mt-1 text-xs text-[var(--color-text-muted)]">
                      {progress.toFixed(1)}{t('budgets.percentUsed')}
                    </div>
                  </div>

                  <div className="mt-4 space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-[var(--color-text-muted)]">{t('budgets.period')}</span>
                      <span className="text-[var(--color-text)]">{getPeriodLabel(budget.period)}</span>
                    </div>
                    <div className="flex justify-between text-sm">
                      <span className="text-[var(--color-text-muted)]">{t('budgets.category')}</span>
                      <span className="text-[var(--color-text)]">{budget.category?.name || t('budgets.allCategoriesValue')}</span>
                    </div>
                    {budget.start_date && (
                      <div className="flex justify-between text-sm">
                        <span className="text-[var(--color-text-muted)]">{t('budgets.startDate')}</span>
                        <span className="text-[var(--color-text)]">{formatDate(budget.start_date)}</span>
                      </div>
                    )}
                  </div>

                  {budget.description && (
                    <div className="mt-4">
                      <p className="text-sm text-[var(--color-text-muted)]">{budget.description}</p>
                    </div>
                  )}
                </Card.Body>
              </Card>
            )
          })}
        </div>
      ) : (
        <Card>
          <Card.Body>
            <div className="text-center py-12">
              <p className="text-[var(--color-text-muted)]">{t('budgets.noData')}</p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="mt-4 inline-flex items-center rounded-md bg-[#1f9f93] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#167e75]"
              >
                {t('budgets.addFirst')}
              </button>
            </div>
          </Card.Body>
        </Card>
      )}

      {/* Modal dodawania/edycji budżetu */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <div className="fixed inset-0 bg-[var(--color-overlay)] transition-opacity" onClick={() => setIsModalOpen(false)}></div>

            <div className="relative transform overflow-hidden rounded-lg bg-[var(--color-surface)] px-4 pb-4 pt-5 text-left shadow-xl transition-all sm:my-8 sm:w-full sm:max-w-lg sm:p-6">
              <form onSubmit={handleSubmit}>
                <div>
                  <div className="mt-3 text-center sm:mt-0 sm:text-left">
                    <h3 className="text-base font-semibold leading-6 text-[var(--color-text)]">
                      {editingBudget ? t('budgets.editTitle') : t('budgets.addTitle')}
                    </h3>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('budgets.nameLabel')}
                        </label>
                        <input
                          type="text"
                          id="name"
                          value={formData.name}
                          onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                          placeholder={t('budgets.namePlaceholder')}
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="amount" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('budgets.amountLabel')}
                        </label>
                        <input
                          type="number"
                          step="0.01"
                          id="amount"
                          value={formData.amount}
                          onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                          placeholder="0.00"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="period" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('budgets.periodLabel')}
                        </label>
                        <select
                          id="period"
                          value={formData.period}
                          onChange={(e) => setFormData({ ...formData, period: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                        >
                          <option value="daily">{t('common:periods.daily')}</option>
                          <option value="weekly">{t('common:periods.weekly')}</option>
                          <option value="monthly">{t('common:periods.monthly')}</option>
                          <option value="yearly">{t('common:periods.yearly')}</option>
                        </select>
                      </div>

                      <div>
                        <label htmlFor="category_id" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('budgets.categoryLabel')}
                        </label>
                        <select
                          id="category_id"
                          value={formData.category_id}
                          onChange={(e) => setFormData({ ...formData, category_id: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                        >
                          <option value="">{t('budgets.allCategories')}</option>
                          {categories && categories.map((category) => (
                            <option key={category.id} value={category.id}>
                              {category.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label htmlFor="start_date" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('budgets.startDateLabel')}
                        </label>
                        <input
                          type="date"
                          id="start_date"
                          value={formData.start_date}
                          onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                          required
                        />
                      </div>

                      <div>
                        <label htmlFor="end_date" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('budgets.endDateLabel')}
                        </label>
                        <input
                          type="date"
                          id="end_date"
                          value={formData.end_date}
                          onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                        />
                      </div>

                      <div>
                        <label htmlFor="description" className="block text-sm font-medium leading-6 text-[var(--color-text)]">
                          {t('budgets.descriptionLabel')}
                        </label>
                        <textarea
                          id="description"
                          rows={3}
                          value={formData.description}
                          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                          className="mt-1 block w-full rounded-md border-0 py-1.5 text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] placeholder:text-[var(--color-text-faint)] focus:ring-2 focus:ring-inset focus:ring-[#1f9f93] sm:text-sm sm:leading-6"
                          placeholder={t('budgets.descriptionPlaceholder')}
                        />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-5 sm:mt-6 sm:grid sm:grid-flow-row-dense sm:grid-cols-2 sm:gap-3">
                  <button
                    type="submit"
                    disabled={createMutation.isLoading || updateMutation.isLoading}
                    className="inline-flex w-full justify-center rounded-md bg-[#1f9f93] px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-[#167e75] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#1f9f93] sm:col-start-2 disabled:opacity-50"
                  >
                    {createMutation.isLoading || updateMutation.isLoading ? t('common:saving') : (editingBudget ? t('common:save') : t('common:add'))}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false)
                      setEditingBudget(null)
                      resetForm()
                    }}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-[var(--color-surface)] px-3 py-2 text-sm font-semibold text-[var(--color-text)] shadow-sm ring-1 ring-inset ring-[var(--color-border)] hover:bg-[var(--color-hover)] sm:col-start-1 sm:mt-0"
                  >
                    {t('common:cancel')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default BudgetsPage
